/**
 * Hook to fetch available batches for Mathlete 101 course
 * Based on starry-night reference implementation
 */

import { useQuery } from '@tanstack/react-query';

import { useProfilesData } from '@/features/user/api/queries';
import { MATHLETE_CONFIG } from '@/features/user-notifications/utils/constants';

import { getMathleteBatches } from '../api/get-mathlete-batches';
import type { MathleteBatch, MathleteBatchesResponse } from '../types';

export interface UseMathleteBatchesResult {
  batches: MathleteBatch[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  data?: MathleteBatchesResponse;
}

/**
 * Finds Mathlete course by course ID from the configuration
 */
const findMathleteCourse = (
  courseId: string
): { name: string; courseId: string; variantId: string } | undefined => {
  return MATHLETE_CONFIG.courses.find(
    course => course.courseId === courseId
  ) as { name: string; courseId: string; variantId: string } | undefined;
};

/**
 * Hook to fetch and manage Mathlete batches for a specific course
 *
 * @param courseId - The course ID to fetch batches for
 * @returns Batch data, loading state, and error information
 *
 * @example
 * ```tsx
 * const { batches, isLoading, isError } = useMathleteBatches('course-123');
 * ```
 */
export const useMathleteBatches = (
  courseId: string
): UseMathleteBatchesResult => {
  const { selectedProfile } = useProfilesData();
  const profileId = selectedProfile?.id ?? '';
  const course = findMathleteCourse(courseId);

  const { data, isLoading, error, isError } = useQuery({
    queryKey: [MATHLETE_CONFIG.queryKeys.batches, course?.variantId, profileId],
    queryFn: () => {
      if (!profileId || !course?.variantId) {
        return Promise.reject(
          new Error('Missing profileId or variantId for batch fetching')
        );
      }
      return getMathleteBatches({
        profile_id: profileId,
        variant_id: course.variantId,
      });
    },
    enabled: Boolean(profileId) && Boolean(course?.variantId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    batches: data?.batches ?? [],
    isLoading,
    isError,
    error,
    data,
  };
};
